import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import Accordion from '../../../components/Accordion';
import SearchInput from '../../../components/SearchInput';
import FundsBox from './components/FundsBox';
import Rollback from '../../../components/Rollback';
import Button from '../../../components/Button';
import UploadedFiles from '../../Dashboard/Reportings/Report/Files';
import { ProjectCard } from './components/ProjectCard';
import {
  Flex,
  FundsItem,
  FundsList,
  Wrapper,
  EllipsisText,
  ScaledIconWrapper,
  FlexHeader
} from './style';
import { ReportPeriodCard } from './components/ReportPeriodCard';
import { UploadFile } from './components/UploadFile';
import { fileIcons } from '../../../consts/fileTypes';
import { useDeleteFunds, useDeleteSheet } from '../../../services/workflow.services';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DeleteConfirmModal } from '../../../components/DeleteConfirmModal';
import deleteSheetImage from '../../../assets/images/delete-sheet.png';
import deleteFileImage from '../../../assets/images/delete-file.png';
import StatusTag from '../../../components/StatusTag';
import { BeatLoader } from 'react-spinners';
import {
  useCreatePeriod,
  useDeletePeriodFile,
  usePeriodById,
  usePeriodFiles,
  useUploadPeriodFiles
} from '../../../services/period.service';
import PageHeader from '../../../components/PageHeader';
import { useGetFundsByPeriodId } from '../../../services/funds.service';

type DeleteAction = 'delete-file' | 'delete-sheet' | 'delete-funds';

interface DeleteVariant {
  title: string;
  description: string;
}

const deleteVariants = (name: string | undefined): Record<DeleteAction, DeleteVariant> => ({
  'delete-file': {
    title: `Are you sure you want to permanently delete '${name}' file?`,
    description:
      'All relevant information will be deleted including data <br/>extractions and reports'
  },
  'delete-sheet': {
    title: `Are you sure you want to permanently delete '${name}' sheet?`,
    description:
      'All relevant information will be deleted including data <br/>extractions and reports'
  },
  'delete-funds': {
    title: `Are you sure you want to permanently delete all funds?`,
    description:
      'All relevant information will be deleted including data <br/>extractions and reports'
  }
});

export default function CreateProjectContainer() {
  const { projectId: workflowId } = useParams();

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const periodId = searchParams.get('periodId');
  const [action, setAction] = useState<DeleteAction | undefined>();
  const [sheet, setSheet] = useState<any>();
  const [file, setFile] = useState<any>();
  const [progress, setProgress] = useState<number>(0);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [year, setYear] = useState<number | null>(null);
  const [period, setPeriod] = useState<string | null>(null);

  const { mutate: mutateDeleteSheet, isLoading: isLoadingDeleteSheet } = useDeleteSheet();

  const { mutate: mutateDeleteFunds, isLoading: isLoadingDeleteFunds } = useDeleteFunds();
  const uploadPeriodFiles = useUploadPeriodFiles();
  const deletePeriodFile = useDeletePeriodFile();
  const createPeriod = useCreatePeriod();

  const workflowFiles = usePeriodFiles({
    periodId,
    queryOptions: {
      refetchInterval: isPaused ? 0 : 5000,
      enabled: !!periodId
    }
  });

  const isNextNotDisabled =
    workflowFiles?.data?.length > 0 &&
    workflowFiles?.data?.every(item => item.status === 'completed' || item.status === 'failed');

  usePeriodById({
    periodId,
    queryOptions: {
      enabled: !!periodId,
      onSuccess: (res: any) => {
        setPeriod(res.period);
        setYear(res.year);
      }
    }
  });

  useEffect(() => {
    if (year && period && workflowId) {
      if (!periodId)
        createPeriod.mutate(
          {
            year,
            period,
            workflow_id: workflowId,
            is_from_template: false
          },
          {
            onSuccess: (res: any) => {
              setSearchParams({
                periodId: res.id
              });
            },
            onError: (err: any) => {
              toast.error(`Failed to create period ${err?.data?.detail}`);
            }
          }
        );
    }
  }, [year, period]);

  useEffect(() => {
    if (isNextNotDisabled) setIsPaused(true);
  }, [isNextNotDisabled]);

  const workflowFunds = useGetFundsByPeriodId({
    periodId: periodId,
    queryParams: {
      // TODO: revisit this interval, it should be converted to SignalR
      refetchInterval: isNextNotDisabled ? 0 : 5000,
      enabled: !!periodId,

      select: (res: any[]) => {
        return res.map(file => ({
          ...file,
          sheets: file.sheets.map((sheet: any) => ({
            ...sheet,
            funds: sheet.funds.filter((fund: any) => !fund.is_deleted)
          }))
        }));
      }
    }
  });

  const onAction = (type?: DeleteAction, file?: any, sheet?: any) => {
    if (type === 'delete-file') {
      setFile(file);
    }
    if (type === 'delete-sheet' || type === 'delete-funds') {
      setSheet(sheet);
    }
    if (!file && !sheet) {
      setFile(undefined);
      setSheet(undefined);
    }
    setAction(type);
  };

  const onConfirmDelete = () => {
    if (action === 'delete-file')
      deletePeriodFile.mutate(
        {
          periodId,
          fileId: file.id
        },
        {
          onSuccess: () => {
            onAction();
            workflowFiles.refetch();
            workflowFunds.refetch();
          },
          onError: (err: any) => {
            toast.error(err.data?.detail || 'Something went wrong.');
          }
        }
      );

    if (action === 'delete-sheet')
      mutateDeleteSheet(sheet.id, {
        onSuccess: () => {
          onAction();
          workflowFunds.refetch();
          workflowFiles.refetch();
        }
      });
    if (action === 'delete-funds')
      mutateDeleteFunds(sheet.id, {
        onSuccess: () => {
          onAction();
          workflowFunds.refetch();
        }
      });
  };

  const onUpdatePeriadAndFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;

    const formData = new FormData();

    if (files) {
      Object.values(files).forEach(file => formData.append('files', file as Blob));
    }

    uploadPeriodFiles.mutate(
      {
        periodId,
        data: formData,
        onUploadProgress: (data: { loaded: number; total: number }) => {
          setProgress(Math.round((100 * data.loaded) / data.total));
        }
      },
      {
        onSuccess: () => {
          setProgress(0);
          workflowFiles.refetch();
          workflowFunds.refetch();
        }
      }
    );
  };

  const onEditFund = () => {
    workflowFunds.refetch();
  };

  const files = workflowFiles?.data;

  useEffect(() => {
    if (files?.length > 0) setSelectedFile(files[0]);
  }, [files]);

  const [selectedFile, setSelectedFile] = useState<any>();

  const onNext = useCallback(() => {
    navigate(`/data-extraction/${workflowId}/${periodId}`);
  }, [period, year, workflowId, periodId]);

  return (
    <>
      <PageHeader title="Upload process" />
      <Wrapper>
        <FlexHeader>
          <Rollback title="Back" onClick={() => navigate(`/project/quaters/${workflowId}`)} />
          <Button size="sm" onClick={onNext} disabled={!isNextNotDisabled} className="no-shrink">
            Next to Extraction
          </Button>
        </FlexHeader>

        <Flex>
          <ProjectCard>
            <ReportPeriodCard
              quarter={period}
              year={year && Number(year)}
              changeQuarter={val => setPeriod(val)}
              changeYear={val => setYear(val)}
            />
          </ProjectCard>
          <UploadFile
            onChange={onUpdatePeriadAndFiles}
            isLoading={uploadPeriodFiles?.isLoading}
            progress={progress}
          />
        </Flex>

        {workflowFiles.isLoading ? (
          <Flex>
            <h4></h4>
          </Flex>
        ) : (
          <Flex>
            {files?.length > 0 && (
              <UploadedFiles
                showTabs={false}
                files={files}
                onDelete={item => onAction('delete-file', item)}
                onSelectFile={setSelectedFile}
                selectedFile={selectedFile}
              />
            )}
          </Flex>
        )}

        {workflowFunds?.isLoading ? (
          <Flex>
            <h4></h4>
          </Flex>
        ) : (
          workflowFunds?.data?.length > 0 &&
          (selectedFile
            ? workflowFunds?.data?.filter(fund => fund.id === selectedFile?.id)
            : workflowFunds?.data
          )?.map((fund, fdx) => (
            <Flex key={fund.id}>
              <FileList
                fund={fund}
                workflowId={workflowId}
                onEditFund={onEditFund}
                fileId={files?.[fdx]?.id}
                file={files?.find(item => item.id === fund.id)}
                onDeleteSheet={sheet => onAction('delete-sheet', selectedFile, sheet)}
                onDeleteFunds={sheet => onAction('delete-funds', selectedFile, sheet)}
              />
            </Flex>
          ))
        )}
      </Wrapper>
      <DeleteConfirmModal
        onClose={onAction}
        image={action === 'delete-file' ? deleteFileImage : deleteSheetImage}
        isOpen={!!action}
        onConfirm={onConfirmDelete}
        isLoading={deletePeriodFile.isLoading || isLoadingDeleteSheet || isLoadingDeleteFunds}
        title={action ? deleteVariants(file?.name || sheet?.name)[action]?.title : ''}
        description={action ? deleteVariants(file?.name || sheet?.name)[action]?.description : ''}
      />
    </>
  );
}

interface FileListProps {
  fund: any;
  workflowId?: string;
  onEditFund: () => void;
  fileId?: string;
  onDeleteSheet: (sheet: any) => void;
  file?: any;
  onDeleteFunds: (sheet: any) => void;
}

const FileList = ({
  fund,
  workflowId,
  onEditFund,
  fileId,
  onDeleteSheet,
  file,
  onDeleteFunds
}: FileListProps) => {
  const [value, setValue] = useState<string>('');

  const FileIcon = fileIcons[fund?.type || 'excel'];

  const filteredFunds = useMemo(() => {
    if (value)
      return fund.sheets?.filter(
        (sheet: any) =>
          sheet?.funds?.filter((item: any) => item.name.toLowerCase().includes(value.toLowerCase()))
            ?.length > 0
      );
    else return fund.sheets;
  }, [value, fund]);

  return (
    <Accordion
      title={
        <>
          <ScaledIconWrapper>
            <FileIcon />
          </ScaledIconWrapper>{' '}
          <EllipsisText>{fund?.name}</EllipsisText>
          {(file?.status === 'in progress' || file?.status === 'failed') && (
            <StatusTag status={file.status} />
          )}
        </>
      }
      actions={
        <>
          {(file?.status === 'completed' || file?.status === 'failed') && (
            <SearchInput
              isWhite={true}
              value={value}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(e.target.value)}
            />
          )}
          {file?.status === 'in progress' && <BeatLoader size={10} />}
        </>
      }
    >
      {(file?.status === 'completed' || file?.status === 'failed') && (
        <FundsList>
          {filteredFunds?.map((sheet: any) => (
            <FundsItem key={sheet.id}>
              <FundsBox
                workflowId={workflowId}
                title={sheet.name}
                filterbyKey={value}
                items={sheet.funds}
                onEditFund={onEditFund}
                sheetId={sheet.id}
                fileId={fileId}
                onDelete={() => onDeleteSheet(sheet)}
                onDeleteFunds={() => onDeleteFunds(sheet)}
              />
            </FundsItem>
          ))}
        </FundsList>
      )}
    </Accordion>
  );
};
